import { parsePhoneNumberFromString as parseMax } from 'libphonenumber-js/max';
import defaultInitialState from './constants/default-state';

/**
 * Normalizes phone number string from API for redux state
 *
 * @param {string|null} phoneNumber
 * @author jozef.repan
 */
export const processPhoneNumber = phoneNumber => {
  const initial = {
    prefix: null,
    number: undefined
  };

  if (phoneNumber === null) {
    return initial;
  }
  const parsedPhoneNumber = parseMax(phoneNumber);
  if (parsedPhoneNumber === undefined) {
    return initial;
  }
  return {
    prefix: parsedPhoneNumber.countryCallingCode,
    number: parsedPhoneNumber.nationalNumber
  };
};

/**
 * Creates full name string
 *
 * @param {string} firstName
 * @param {*} middleName
 * @param {*} lastName
 * @author jozef.repan
 */
export const createFullName = (firstName, middleName, lastName) => {
  let fullName = '';

  if (firstName !== null) {
    fullName += firstName;
  }

  if (middleName !== null) {
    if (fullName.length > 0) fullName += ' ';
    fullName += `${middleName}`;
  }

  if (lastName !== null) {
    if (fullName.length > 0) fullName += ' ';
    fullName += `${lastName}`;
  }
  return fullName;
};

/**
 * Normalizes birthday string from API for redux state
 * TODO 'Invalid date' will be fixed on the server
 *
 * @param {string} birthday expects birthday in 'YYYY-MM-DD' format
 * @returns {} object with properties 'day', 'month' and 'year'
 * @author jozef.repan
 */
export const processBirthday = birthday => {
  if (birthday === null || birthday === 'Invalid date') {
    return {
      day: null,
      month: null,
      year: null
    };
  }
  const splitted = birthday.split('-');
  return {
    day: splitted[2],
    month: splitted[1],
    year: splitted[0]
  };
};

/**
 * Normalizes KYC status from API for redux state
 *
 * @param kycData data
 * @author jozef.repan
 */
export const processKycStatusRequest = kycData => {
  return {
    id: kycData.id,
    status: kycData.status,
    documents: kycData.documents || null,
    approvedAddress: kycData.approvedAddress,
    approvedIdentity: kycData.approvedIdentity,
    approvedSelfie: kycData.approvedSelfie,
    updatedAt: kycData.updatedAt,
    createdAt: kycData.createdAt,
    ...(kycData.allowedTypes && { allowedTypes: kycData.allowedTypes }),
    ...(kycData.subtypes && { subtypes: kycData.subtypes })
  };
};

/**
 * Resolves, which 2FA opts are DISABLED (non-editable) and normalizes the data from API for redux state
 * This is called when app is initialized
 * Call: /area/client/authorization-rule
 * @param {object} data from API
 * @return {object} enchanced data for redux state
 * @author jozef.repan
 */
export const processTwoFaDisabledInfo = data => {
  // get default state and enchance it by data from API
  const result = defaultInitialState.user.auth;

  if (data.email_verification) {
    result.emailVerificationDisabled = data.email_verification;
  }
  if (data.phone_number_verification) {
    result.smsVerificationDisabled = data.phone_number_verification;
  }
  if (data.u2f) {
    result.u2fVerificationDisabled = data.u2f;
  }
  if (data.totp) {
    result.totpVerificationDisabled = data.totp;
  }
  return result;
};

/**
 * Resolves, which 2FA opts are CHECKED and normalizes the data from API for redux state
 * This is called when app is initialized
 * Call: /area/user/authorization-rule
 * @param {object} data from API
 * @return {object} enchanced data for redux state
 * @author jozef.repan
 */
export const processTwoFaCheckedInfo = (prevState, payload, u2fUrl) => {
  // get default state and enchance it by data from API
  const result = prevState;

  if (payload.email_verification) {
    result.emailVerificationChecked = payload.email_verification;
  }
  if (payload.phone_number_verification) {
    result.smsVerificationChecked = payload.phone_number_verification;
  }
  if (payload.u2f) {
    result.u2fVerificationChecked = payload.u2f;
  }
  if (payload.totp) {
    result.totpVerificationChecked = payload.totp;
  }

  result.u2fUrl = u2fUrl;

  return result;
};

/**
 * Resolves update of an 2FA option
 *
 * @param prevState {object} previous state we are updating
 * @param data {object} orignal request
 * @return {object} enchanced data for redux state
 * @author jozef.repan
 */
export const processTwoFaCheckInfo = (prevState, { type, value }) => {
  const result = prevState;

  switch (type) {
    case 'email':
      result.emailVerificationChecked = value;
      break;
    case 'sms':
      result.smsVerificationChecked = value;
      break;
    case 'totp':
      result.totpVerificationChecked = value;
      break;
    case 'u2f':
      result.u2fVerificationChecked = value;
      break;
    default:
      break;
  }
  return result;
};

/**
 * Function to parse updated profile from server to redux structure
 * @param {object} profile updated profile from server
 * @author boris.nanista
 */
export function parseGetUserRequestData(profile) {
  return {
    fullName: createFullName(
      profile.first_name,
      profile.middle_name,
      profile.last_name
    ),
    firstName: profile.first_name,
    middleName: profile.middle_name,
    lastName: profile.last_name,
    nationality: profile.nationality,
    email: profile.email,
    username: profile.username,
    phone: processPhoneNumber(profile.phone_number),
    address: {
      street: profile.address,
      city: profile.city,
      country: profile.country_code,
      zip: profile.post_code
    },
    birthday: processBirthday(profile.date_of_birth),
    refs: profile.refs
  };
}
